/// <amd-module name="Comments/Redux/CommentsReducer" />
import { IComment, ICommentArtifactInfo, ICommentReaction } from "Comments/Common/Contracts";
import { createReactionsMap } from "Comments/Reactions/Util";
import { CommentsActions, CommentsActionTypes } from "Comments/Redux/CommentsActions";
import { getArtifactKey, getCommentItemStateFromMap } from "Comments/Redux/CommentsSelectors";
import { ICommentItemReduxState, ICommentsReduxState, ICommentsReduxStateMap } from "Comments/Redux/Contracts";
import produce from "immer";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";

export const defaultCommentsReduxStateMap: ICommentsReduxStateMap = {};

export function commentsReducer(state: ICommentsReduxStateMap, action: CommentsActions): ICommentsReduxStateMap {
    return produce(state || defaultCommentsReduxStateMap, (draft: ICommentsReduxStateMap) => {
        switch (action.type) {
            case CommentsActionTypes.Load: {
                const { artifactInfo, comments = [] } = action.payload;
                const artifactKey = getArtifactKey(artifactInfo);

                // load a new list of comments for the artifact
                const commentState: ICommentsReduxState = (draft[artifactKey] = { comments: {} });
                _addCommentIfNewer(commentState, comments);

                break;
            }
            case CommentsActionTypes.AddItem: {
                const { artifactInfo, comment, isEditing } = action.payload;

                const commentState: ICommentsReduxState = _getOrCreateCommentsStateFromMap(draft, artifactInfo);
                _addCommentIfNewer(commentState, [comment]);
                commentState.comments[comment.id]!.isEditing = isEditing;

                break;
            }
            case CommentsActionTypes.AddList: {
                const { artifactInfo, comments = [] } = action.payload;

                const commentState: ICommentsReduxState = _getOrCreateCommentsStateFromMap(draft, artifactInfo);
                _addCommentIfNewer(commentState, comments);

                break;
            }
            case CommentsActionTypes.AddPage: {
                const { artifactInfo, commentPage } = action.payload;

                const commentState: ICommentsReduxState = _getOrCreateCommentsStateFromMap(draft, artifactInfo);
                if (commentPage) {
                    const { comments, continuationToken, totalCount } = commentPage;
                    _addCommentIfNewer(commentState, comments);
                    commentState.paging = { continuationToken, totalCount };
                }

                break;
            }
            case CommentsActionTypes.StartEditItem: {
                const { artifactInfo, commentId } = action.payload;
                const commentItemState: ICommentItemReduxState | null | undefined = getCommentItemStateFromMap(
                    draft,
                    artifactInfo,
                    commentId
                );
                if (commentItemState) {
                    commentItemState.isEditing = true;
                }

                break;
            }
            case CommentsActionTypes.CancelEditItem: {
                const { artifactInfo, commentId } = action.payload;
                const commentItemState: ICommentItemReduxState | null | undefined = getCommentItemStateFromMap(
                    draft,
                    artifactInfo,
                    commentId
                );
                if (commentItemState) {
                    commentItemState.isEditing = false;
                }

                break;
            }
            case CommentsActionTypes.UpdateItem: {
                const { artifactInfo, comment } = action.payload;
                const commentItemState: ICommentItemReduxState | null | undefined = getCommentItemStateFromMap(
                    draft,
                    artifactInfo,
                    comment.id
                );
                if (commentItemState) {
                    commentItemState.comment = comment;
                    commentItemState.isEditing = false;
                }

                break;
            }
            case CommentsActionTypes.DeleteItem: {
                const { artifactInfo, commentId } = action.payload;
                const commentsState: ICommentsReduxState = _getOrCreateCommentsStateFromMap(draft, artifactInfo);
                const commentState = commentsState.comments[commentId];
                if (commentState && commentState.comment) {
                    commentState.comment.isDeleted = true;
                } else {
                    commentsState.comments[commentId] = null; // even if comment is not in the store, mark it as null for deleted
                }

                break;
            }

            case CommentsActionTypes.PendingReactionActionCallsInitiated: {
                const { artifactInfo, commentId } = action.payload;
                const commentItemState: ICommentItemReduxState | null | undefined = getCommentItemStateFromMap(
                    draft,
                    artifactInfo,
                    commentId
                );
                if (commentItemState) {
                    commentItemState.isPendingReactionActionUpdatesInitiated = true;
                }

                break;
            }

            case CommentsActionTypes.UpdateReaction: {
                const { artifactInfo, updateReactionResponse, currentUser, isAdded, isLocalUpdate } = action.payload;
                const commentItemState: ICommentItemReduxState | null | undefined = getCommentItemStateFromMap(
                    draft,
                    artifactInfo,
                    updateReactionResponse.commentId
                );
                if (commentItemState) {
                    _updateCommentReaction(commentItemState, updateReactionResponse, currentUser, isAdded, isLocalUpdate);
                }

                break;
            }

            case CommentsActionTypes.AddRecentlyReactedUsers: {
                const { artifactInfo, commentId, reactionType, recentEngagedUsers } = action.payload;
                const commentItemState: ICommentItemReduxState | null | undefined = getCommentItemStateFromMap(
                    draft,
                    artifactInfo,
                    commentId
                );

                if (commentItemState) {
                    const reactionTypeInfoMap = commentItemState.commentReactionTypeToInfoMap;
                    reactionTypeInfoMap[reactionType].engagedUsers = recentEngagedUsers.map((identityRef) => {
                        return { identityId: identityRef.id, identityDisplayName: identityRef.displayName };
                    });
                    reactionTypeInfoMap[reactionType].totalCount = recentEngagedUsers.length;
                }

                break;
            }
        }
    });
}

function _getOrCreateCommentsStateFromMap(state: ICommentsReduxStateMap, artifactInfo: ICommentArtifactInfo): ICommentsReduxState {
    const artifactKey = getArtifactKey(artifactInfo);

    if (!state[artifactKey]) {
        state[artifactKey] = { comments: {}, continuationToken: "", totalCount: 0 } as ICommentsReduxState;
    }

    return state[artifactKey];
}

// adds if newer and also replace existing (in case of replacig local changes with final server changes)
function _addCommentIfNewer(state: ICommentsReduxState, comments: IComment[]) {
    for (const comment of comments) {
        const existingComment = state.comments[comment.id];
        if (existingComment && existingComment.comment && existingComment.comment.version > comment.version) {
            continue;
        }

        const commentReactionTypeToInfoMap = createReactionsMap(comment.reactions);

        state.comments[comment.id] = {
            comment,
            isEditing: false,
            commentReactionTypeToInfoMap,
            isPendingReactionActionUpdatesInitiated: false
        } as ICommentItemReduxState;
    }
}

function _updateCommentReaction(
    commentItemState: ICommentItemReduxState,
    updateReactionResponse: ICommentReaction,
    currentUser: IdentityRef,
    isAdded: boolean,
    isLocalUpdate: boolean
): void {
    const reactionType = updateReactionResponse.type;
    const reactionTypeInfoMap = commentItemState.commentReactionTypeToInfoMap;
    const reactionTypeInfo = reactionTypeInfoMap[reactionType];

    if (isLocalUpdate && reactionTypeInfo) {
        const engagedUsers = reactionTypeInfo.engagedUsers;
        const reactionCountIncrementValue: number = isAdded ? 1 : -1;

        const currentUserId = currentUser.id;
        const currentUserDisplayName = currentUser.displayName;
        const userIndex = engagedUsers.findIndex((identityInfo) => identityInfo.identityId.toUpperCase() === currentUserId.toUpperCase());
        if (userIndex !== -1) {
            engagedUsers.splice(userIndex, 1); // we add back the current user at the beginning of the array below in the case of 'Add reaction'
        }
        reactionTypeInfo.totalCount = reactionTypeInfo.totalCount + reactionCountIncrementValue;
        reactionTypeInfo.isCurrentUserEngaged = isAdded;

        if (isAdded) {
            engagedUsers.unshift({ identityId: currentUserId, identityDisplayName: currentUserDisplayName });
        }
    } else {
        reactionTypeInfoMap[reactionType] = {
            totalCount: updateReactionResponse.count,
            isCurrentUserEngaged: updateReactionResponse.isCurrentUserEngaged,
            engagedUsers: [] // Reset users list to empty on server response as the list might have changed
        };
    }
}
